// src/utils/locationService.ts
import { indexedDBService, LocationEntry } from './indexedDB';

export interface LocationPermissionStatus {
  granted: boolean;
  denied: boolean;
  prompt: boolean;
  available: boolean;
}

export interface GeolocationPosition {
  latitude: number;
  longitude: number;
  accuracy: number;
  altitude?: number;
  altitudeAccuracy?: number;
  heading?: number;
  speed?: number;
  timestamp: number;
}

export interface GeofenceArea {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
  radius: number; // in meters
  type: 'work_site' | 'office' | 'restricted';
}

export interface LocationOptions {
  enableHighAccuracy?: boolean;
  timeout?: number;
  maximumAge?: number;
}

class LocationService {
  private watchId: number | null = null;
  private lastKnownPosition: GeolocationPosition | null = null;
  private geofences: GeofenceArea[] = [];
  private isTracking = false;

  constructor() {
    this.loadGeofences();
  }

  // Check if geolocation is available
  isGeolocationAvailable(): boolean {
    return 'geolocation' in navigator;
  }

  // Get current permission status
  async getPermissionStatus(): Promise<LocationPermissionStatus> {
    if (!this.isGeolocationAvailable()) {
      return {
        granted: false,
        denied: false,
        prompt: false,
        available: false,
      };
    }

    // Check if permissions API is available
    if ('permissions' in navigator) {
      try {
        const permission = await navigator.permissions.query({ name: 'geolocation' });
        return {
          granted: permission.state === 'granted',
          denied: permission.state === 'denied',
          prompt: permission.state === 'prompt',
          available: true,
        };
      } catch (error) {
        console.warn('Failed to query geolocation permission:', error);
      }
    }

    // Fallback: Try to get position to determine permission
    try {
      await this.getCurrentPosition({ timeout: 1000 });
      return {
        granted: true,
        denied: false,
        prompt: false,
        available: true,
      };
    } catch (error) {
      const err = error as GeolocationPositionError;
      return {
        granted: false,
        denied: err.code === err.PERMISSION_DENIED,
        prompt: err.code !== err.PERMISSION_DENIED,
        available: true,
      };
    }
  }

  // Request location permission
  async requestPermission(): Promise<LocationPermissionStatus> {
    if (!this.isGeolocationAvailable()) {
      throw new Error('Geolocation is not available in this browser');
    }

    try {
      // Requesting position will trigger permission prompt if needed
      const position = await this.getCurrentPosition({
        enableHighAccuracy: false,
        timeout: 10000,
      });

      this.lastKnownPosition = this.formatPosition(position);
      
      return {
        granted: true,
        denied: false,
        prompt: false,
        available: true,
      };
    } catch (error) {
      const err = error as GeolocationPositionError;
      
      return {
        granted: false,
        denied: err.code === err.PERMISSION_DENIED,
        prompt: false,
        available: true,
      };
    }
  }

  // Get current position
  async getCurrentPosition(options: LocationOptions = {}): Promise<GeolocationPosition> {
    if (!this.isGeolocationAvailable()) {
      throw new Error('Geolocation is not available');
    }

    const defaultOptions: PositionOptions = {
      enableHighAccuracy: true,
      timeout: 15000,
      maximumAge: 300000, // 5 minutes
      ...options,
    };

    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          //@ts-ignore
          const formattedPosition = this.formatPosition(position);
          this.lastKnownPosition = formattedPosition;
          
          // Store location in IndexedDB
          this.storeLocation(formattedPosition);
          
          resolve(formattedPosition);
        },
        (error) => {
          console.error('Geolocation error:', error);
          reject(error);
        },
        defaultOptions
      );
    });
  }

  // Start watching position
  async startWatching(options: LocationOptions = {}): Promise<void> {
    if (!this.isGeolocationAvailable()) {
      throw new Error('Geolocation is not available');
    }

    if (this.isTracking) {
      return;
    }

    const defaultOptions: PositionOptions = {
      enableHighAccuracy: true,
      timeout: 30000,
      maximumAge: 60000, // 1 minute
      ...options,
    };

    this.watchId = navigator.geolocation.watchPosition(
      (position) => {
        //@ts-ignore
        const formattedPosition = this.formatPosition(position);
        this.lastKnownPosition = formattedPosition;
        
        // Store location in IndexedDB
        this.storeLocation(formattedPosition);
        
        // Check geofences
        this.checkGeofences(formattedPosition);
        
        // Dispatch custom event
        window.dispatchEvent(new CustomEvent('locationUpdate', {
          detail: formattedPosition
        }));
      },
      (error) => {
        console.error('Watch position error:', error);
        window.dispatchEvent(new CustomEvent('locationError', {
          detail: error
        }));
      },
      defaultOptions
    );

    this.isTracking = true;
  }

  // Stop watching position
  stopWatching(): void {
    if (this.watchId !== null) {
      navigator.geolocation.clearWatch(this.watchId);
      this.watchId = null;
      this.isTracking = false;
    }
  }

  // Get last known position
  getLastKnownPosition(): GeolocationPosition | null {
    return this.lastKnownPosition;
  }

  // Store location in IndexedDB
  private async storeLocation(position: GeolocationPosition): Promise<void> {
    try {
      const userId = localStorage.getItem('userId') || 'unknown';
      const locationEntry: LocationEntry = {
        userId,
        latitude: position.latitude,
        longitude: position.longitude,
        accuracy: position.accuracy,
        timestamp: new Date().toISOString(),
        synced: false,
      };

      await indexedDBService.addLocation(locationEntry);
      
      // Queue for background sync if offline
      if (!navigator.onLine) {
      }
    } catch (error) {
    }
  }

  // Format position from native API
  private formatPosition(position: GeolocationPosition): GeolocationPosition {
    return {
      latitude: (position as any).coords.latitude,
      longitude: (position as any).coords.longitude,
      accuracy: (position as any).coords.accuracy,
      altitude: (position as any).coords.altitude || undefined,
      altitudeAccuracy: (position as any).coords.altitudeAccuracy || undefined,
      heading: (position as any).coords.heading || undefined,
      speed: (position as any).coords.speed || undefined,
      timestamp: position.timestamp,
    };
  }

  // Geofencing methods
  setGeofences(geofences: GeofenceArea[]): void {
    this.geofences = geofences;
    localStorage.setItem('geofences', JSON.stringify(geofences));
  }

  addGeofence(geofence: GeofenceArea): void {
    this.geofences.push(geofence);
    localStorage.setItem('geofences', JSON.stringify(this.geofences));
  }

  removeGeofence(id: string): void {
    this.geofences = this.geofences.filter(g => g.id !== id);
    localStorage.setItem('geofences', JSON.stringify(this.geofences));
  }

  private loadGeofences(): void {
    try {
      const stored = localStorage.getItem('geofences');
      if (stored) {
        this.geofences = JSON.parse(stored);
      }
    } catch (error) {
      console.error('Failed to load geofences:', error);
    }
  }

  // Check if current position is within any geofences
  private checkGeofences(position: GeolocationPosition): void {
    for (const geofence of this.geofences) {
      const distance = this.calculateDistance(
        position.latitude,
        position.longitude,
        geofence.latitude,
        geofence.longitude
      );

      const isInside = distance <= geofence.radius;
      const wasInside = this.getGeofenceStatus(geofence.id);

      if (isInside && !wasInside) {
        // Entered geofence
        this.setGeofenceStatus(geofence.id, true);
        window.dispatchEvent(new CustomEvent('geofenceEnter', {
          detail: { geofence, position, distance }
        }));
      } else if (!isInside && wasInside) {
        // Exited geofence
        this.setGeofenceStatus(geofence.id, false);
        window.dispatchEvent(new CustomEvent('geofenceExit', {
          detail: { geofence, position, distance }
        }));
      }
    }
  }

  // Calculate distance between two points (Haversine formula)
  private calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = (lat1 * Math.PI) / 180;
    const φ2 = (lat2 * Math.PI) / 180;
    const Δφ = ((lat2 - lat1) * Math.PI) / 180;
    const Δλ = ((lon2 - lon1) * Math.PI) / 180;

    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
  }

  // Geofence status management
  private getGeofenceStatus(geofenceId: string): boolean {
    const status = localStorage.getItem(`geofence_${geofenceId}`);
    return status === 'true';
  }

  private setGeofenceStatus(geofenceId: string, inside: boolean): void {
    localStorage.setItem(`geofence_${geofenceId}`, inside.toString());
  }

  // Get current location with address (requires reverse geocoding)
  async getCurrentLocationWithAddress(options: LocationOptions = {}): Promise<GeolocationPosition & { address?: string }> {
    const position = await this.getCurrentPosition(options);
    
    try {
      const address = await this.reverseGeocode(position.latitude, position.longitude);
      return { ...position, address };
    } catch (error) {
      console.warn('Failed to get address:', error);
      return position;
    }
  }

  // Reverse geocoding (requires external service or browser API)
  private async reverseGeocode(latitude: number, longitude: number): Promise<string> {
    // This is a placeholder - you'd implement with your preferred geocoding service
    // Options: Google Maps Geocoding API, OpenStreetMap Nominatim, etc.
    
    try {
      // Example with OpenStreetMap Nominatim (free but has usage limits)
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`
      );
      
      if (response.ok) {
        const data = await response.json();
        return data.display_name || 'Unknown location';
      }
    } catch (error) {
      console.warn('Reverse geocoding failed:', error);
    }
    
    return `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
  }

  // Check if user is at a specific location (within radius)
  async isAtLocation(targetLat: number, targetLon: number, radiusMeters: number = 100): Promise<boolean> {
    try {
      const position = await this.getCurrentPosition();
      const distance = this.calculateDistance(
        position.latitude,
        position.longitude,
        targetLat,
        targetLon
      );
      return distance <= radiusMeters;
    } catch (error) {
      console.error('Failed to check location:', error);
      return false;
    }
  }

  // Get location accuracy status
  getAccuracyStatus(accuracy: number): 'excellent' | 'good' | 'fair' | 'poor' {
    if (accuracy <= 5) return 'excellent';
    if (accuracy <= 10) return 'good';
    if (accuracy <= 50) return 'fair';
    return 'poor';
  }

  // Location tracking status
  isCurrentlyTracking(): boolean {
    return this.isTracking;
  }

  // Clean up
  cleanup(): void {
    this.stopWatching();
    this.lastKnownPosition = null;
  }
}

// Export singleton instance
export const locationService = new LocationService();